import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import VoiceMic from "../components/VoiceMic";
import { MODULE_DEFS } from "../lib/modules";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import { errMsg } from "../lib/errors";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useAria } from "../context/AriaContext";
import { Loader2, Download, FileText, User, Paperclip, ImageIcon, Upload, Trash2 } from "lucide-react";
import { jsPDF } from "jspdf";
import { ACCEPT_EXTENSIONS, CATEGORY_META, humanSize, PHOTO_SUBTYPE_OPTIONS, REALGUIDE_MODULES } from "../lib/files";
import { uploadManyWithProgress } from "../lib/upload";
import { decoratePdfWithDisclaimer } from "../lib/pdf";
import UploadProgressList from "../components/UploadProgressList";
import UploadExtras from "../components/UploadExtras";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { useRef } from "react";

export default function Module() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const initialPatientId = params.get("patient_id") || "";
  const nav = useNavigate();
  const { user } = useAuth();
  const aria = useAria();
  const def = MODULE_DEFS[slug];
  const [inputs, setInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Patient + imaging attachment state
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState(initialPatientId);
  const [patientFiles, setPatientFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]); // per-file progress rows
  const fileInputRef = useRef(null);
  const showRealguide = REALGUIDE_MODULES.has(slug);

  useEffect(() => {
    setInputs({});
    setResult(null);
    setSelectedFileIds([]);
  }, [slug]);

  // ARIA — brief clinical orientation when this module is opened
  useEffect(() => {
    if (def) aria.greetModule(slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, def]);

  useEffect(() => {
    api
      .get("/patients")
      .then((r) => setPatients(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!patientId) {
      setPatientFiles([]);
      setSelectedFileIds([]);
      return;
    }
    api
      .get(`/patients/${patientId}/files`)
      .then((r) => setPatientFiles(r.data))
      .catch(() => setPatientFiles([]));
    setSelectedFileIds([]);
  }, [patientId]);

  if (!def) {
    return (
      <Layout>
        <div className="text-[var(--pa-text-muted)]">Module not found.</div>
        <Link to="/dashboard" className="text-[var(--pa-gold)] underline">Back to dashboard</Link>
      </Layout>
    );
  }

  const setField = (name, value) => setInputs((p) => ({ ...p, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = def.fields.filter((f) => f.required && !inputs[f.name]?.toString().trim());
    if (missing.length) {
      toast.error(`Required: ${missing.map((m) => m.label).join(", ")}`);
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/consultations", {
        module_slug: slug,
        inputs,
        patient_id: patientId || null,
        file_ids: selectedFileIds,
      });
      setResult(r.data);
      toast.success("Consultation ready");
    } catch (err) {
      toast.error(errMsg(err, "AI service error"));
    } finally {
      setLoading(false);
    }
  };

  const toggleFile = (fid) =>
    setSelectedFileIds((p) =>
      p.includes(fid) ? p.filter((x) => x !== fid) : [...p, fid]
    );

  // Auto-create a "Quick Case" patient if needed so the clinician can upload
  // mid-module without leaving the page.
  const ensurePatientId = async () => {
    if (patientId) return patientId;
    const stamp = new Date().toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
    const r = await api.post("/patients", { name: `Quick Case — ${stamp}` });
    setPatients((prev) => [r.data, ...prev]);
    setPatientId(r.data.id);
    return r.data.id;
  };

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const pid = await ensurePatientId();
      const items = Array.from(fileList).map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
        size: f.size,
      }));
      setUploadItems(
        items.map((it) => ({ id: it.id, name: it.name, pct: 0, status: "uploading" }))
      );
      const uploaded = [];
      const results = await uploadManyWithProgress(
        `/patients/${pid}/files`,
        items,
        {
          concurrency: 3,
          onItemProgress: (id, pct) =>
            setUploadItems((prev) =>
              prev.map((it) => (it.id === id ? { ...it, pct } : it))
            ),
          onItemSuccess: (id, data) => {
            setUploadItems((prev) =>
              prev.map((it) =>
                it.id === id ? { ...it, pct: 100, status: "done" } : it
              )
            );
            uploaded.push(data);
          },
          onItemError: (id, error) => {
            setUploadItems((prev) =>
              prev.map((it) =>
                it.id === id ? { ...it, status: "error", error } : it
              )
            );
            toast.error(error);
          },
        }
      );
      if (uploaded.length) {
        setPatientFiles((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          return [...uploaded.filter((u) => !seen.has(u.id)), ...prev];
        });
        setSelectedFileIds((prev) => {
          const seen = new Set(prev);
          return [...prev, ...uploaded.filter((u) => !seen.has(u.id)).map((u) => u.id)];
        });
        toast.success(`${uploaded.length} file(s) attached`);
      }
      // Clear progress rows after a short delay so the user sees the green ticks
      setTimeout(() => setUploadItems([]), 1500);
      void results;
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const setFileSubtype = async (fid, subtype) => {
    setPatientFiles((prev) =>
      prev.map((f) =>
        f.id === fid
          ? {
              ...f,
              photo_subtype: subtype || null,
              category:
                subtype === "study_model_mounted" || subtype === "wax_up"
                  ? "study_model"
                  : subtype
                  ? "clinical_photo"
                  : f.category,
            }
          : f,
      ),
    );
    try {
      const r = await api.patch(`/files/${fid}`, { photo_subtype: subtype });
      setPatientFiles((prev) => prev.map((f) => (f.id === fid ? r.data : f)));
    } catch (err) {
      toast.error(errMsg(err, "Could not update photo label"));
    }
  };

  const removeUploadedFile = async (fid) => {
    try {
      await api.delete(`/files/${fid}`);
    } catch (_) { /* best-effort */ }
    setPatientFiles((prev) => prev.filter((x) => x.id !== fid));
    setSelectedFileIds((prev) => prev.filter((x) => x !== fid));
  };

  const exportPdf = () => {
    if (!result) return;
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 54;
    const width = 612 - margin * 2;
    let y = margin;

    doc.setFont("times", "bold");
    doc.setFontSize(18);
    doc.text("ProsthoAI Consultation", margin, y);
    y += 22;
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.text(`${result.module_name}`, margin, y);
    y += 14;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date(result.created_at).toLocaleString()}`, margin, y);
    if (user?.full_name) {
      y += 12;
      doc.text(`Prepared for: ${user.full_name}`, margin, y);
    }
    y += 18;
    doc.setDrawColor(212, 175, 55);
    doc.line(margin, y, 612 - margin, y);
    y += 16;

    doc.setTextColor(40);
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.text("Clinical Inputs", margin, y);
    y += 14;
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    Object.entries(result.inputs).forEach(([k, v]) => {
      const label = k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      const line = doc.splitTextToSize(`• ${label}: ${v}`, width);
      if (y + line.length * 12 > 750) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += line.length * 12 + 2;
    });

    y += 10;
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.text("Specialist Analysis", margin, y);
    y += 14;
    doc.setFont("times", "normal");
    doc.setFontSize(10.5);
    const cleaned = result.response.replace(/[#*`]/g, "");
    const lines = doc.splitTextToSize(cleaned, width);
    lines.forEach((ln) => {
      if (y > 740) {
        doc.addPage();
        y = margin;
      }
      doc.text(ln, margin, y);
      y += 13;
    });

    if (y > 720) {
      doc.addPage();
      y = margin;
    }
    y += 20;
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      "Patent Pending US Application 64/062,147 · Dr. Ronik Seecharan DMD, Prosthodontist — 30 years of clinical practice dedicated to treatment planning excellence",
      margin,
      y
    );
    y += 10;
    doc.text(
      "Clinical decision support tool. Not a substitute for licensed professional judgment.",
      margin,
      y
    );

    // Apply mandatory Clinical Decision Support disclaimer header + watermark + footer
    decoratePdfWithDisclaimer(doc);

    doc.save(`prosthoai-${slug}-${result.id.slice(0, 8)}.pdf`);
  };

  const renderField = (f) => {
    const common = {
      value: inputs[f.name] ?? "",
      onChange: (e) => setField(f.name, e.target.value),
      placeholder: f.placeholder,
      "data-testid": `field-${f.name}`,
      className:
        "w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5",
    };

    if (f.type === "select") {
      return (
        <select {...common}>
          <option value="">Select…</option>
          {f.options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
    }

    const setVal = (v) => setField(f.name, v);
    const fieldValue = inputs[f.name] ?? "";

    if (f.type === "textarea") {
      return (
        <div className="relative">
          <textarea rows={3} {...common} className={common.className + " pr-10"} />
          <div className="absolute top-2 right-2">
            <VoiceMic value={fieldValue} onChange={setVal} testIdSuffix={f.name} />
          </div>
        </div>
      );
    }

    return (
      <div className="relative">
        <input type={f.type || "text"} {...common} className={common.className + " pr-10"} />
        <div className="absolute top-1/2 -translate-y-1/2 right-2">
          <VoiceMic value={fieldValue} onChange={setVal} testIdSuffix={f.name} />
        </div>
      </div>
    );
  };

  const Icon = def.Icon;

  return (
    <Layout>
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-md bg-[var(--pa-surface)] border border-[var(--pa-border)]">
          <Icon size={22} className="text-[var(--pa-gold)]" />
        </div>
        <div className="flex-1">
          <div className="kicker">Clinical Module</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-1" data-testid="module-title">
            {def.name}
          </h1>
          <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-2xl">
            {def.description}
          </p>
        </div>
      </div>

      <div className="gold-divider my-8" />

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Input form */}
        <form
          onSubmit={submit}
          className="lg:col-span-5 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6 h-fit"
          data-testid="module-form"
        >
          <div className="kicker mb-1">Clinical Inputs</div>
          <div className="text-[10px] text-[var(--pa-text-dim)] mb-5 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--pa-gold)]" />
            Tap the mic in any field to dictate (Chrome / Edge / Safari)
          </div>

          {/* Patient picker */}
          <div className="space-y-4 pb-5 mb-5 border-b border-[var(--pa-border)]">
            <div>
              <label className="block text-xs font-semibold text-[var(--pa-text-muted)] mb-1.5">
                <User size={11} className="inline mr-1 -mt-0.5" /> Link to patient (optional)
              </label>
              <select
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                data-testid="module-patient-select"
                className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
              >
                <option value="">— No patient linked —</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.dob ? ` · DOB ${p.dob}` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Inline upload + attach list (new) */}
            <div>
              <label className="block text-xs font-semibold text-[var(--pa-text-muted)] mb-1.5">
                <Paperclip size={11} className="inline mr-1 -mt-0.5" /> Clinical Photographs &amp; Study Models
              </label>
              <p className="text-[10px] text-[var(--pa-text-dim)] mb-2 leading-relaxed">
                Upload all available images for the most accurate analysis. More information leads
                to better diagnosis.
                {showRealguide && (
                  <>
                    {" "}
                    <span className="text-emerald-400">
                      Real Guide Planning File — Upload your lab&rsquo;s Real Guide export for
                      prosthodontic verification of implant positioning.
                    </span>
                  </>
                )}
              </p>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  uploadFiles(e.dataTransfer.files);
                }}
                data-testid="module-upload-dropzone"
                className={`cursor-pointer rounded-md border-2 border-dashed p-3 text-center transition mb-2 ${
                  uploading
                    ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5 pointer-events-none"
                    : "border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPT_EXTENSIONS}
                  onChange={(e) => uploadFiles(e.target.files)}
                  data-testid="module-upload-input"
                  className="hidden"
                />
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-xs text-[var(--pa-text-muted)]">
                    <Loader2 size={12} className="animate-spin text-[var(--pa-gold)]" />
                    Uploading {uploadItems.filter((i) => i.status === "done").length} / {uploadItems.length}…
                  </div>
                ) : (
                  <div className="text-xs text-[var(--pa-text-muted)]">
                    <Upload size={14} className="inline mr-1 -mt-0.5 text-[var(--pa-gold)]" />
                    Click to upload photos, radiographs, scans, or lab files
                  </div>
                )}
              </div>

              <UploadProgressList items={uploadItems} dataTestId="module-upload-progress" />

              <UploadExtras
                patientId={patientId || null}
                onFiles={(files) => uploadFiles(files)}
                onFileImported={(rec) => {
                  setPatientFiles((prev) =>
                    prev.find((x) => x.id === rec.id) ? prev : [rec, ...prev]
                  );
                  setSelectedFileIds((prev) =>
                    prev.includes(rec.id) ? prev : [...prev, rec.id]
                  );
                }}
              />

              {patientFiles.length > 0 && (
                <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1" data-testid="module-file-picker">
                  {patientFiles.map((f) => {
                    const meta = CATEGORY_META[f.category] || CATEGORY_META.other;
                    const FIcon = meta.Icon;
                    const checked = selectedFileIds.includes(f.id);
                    const isPhoto = /\.(jpe?g|png|webp)$/i.test(f.original_filename || "");
                    const vision = ["radiograph_2d", "clinical_photo", "study_model"].includes(
                      f.category,
                    );
                    const isRealguide = (f.category || "").startsWith("realguide_");
                    return (
                      <div
                        key={f.id}
                        data-testid={`attach-file-${f.id}`}
                        className={`flex flex-wrap items-center gap-1.5 text-[10px] p-2 rounded-md border transition ${
                          checked
                            ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5"
                            : "border-[var(--pa-border)] hover:border-[var(--pa-border-active)]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFile(f.id)}
                          className="accent-[var(--pa-gold)]"
                        />
                        <FIcon size={12} style={{ color: meta.color }} className="shrink-0" />
                        <div className="flex-1 min-w-0 min-w-[8rem]">
                          <div className="text-[var(--pa-text)] truncate">{f.original_filename}</div>
                          <div className="text-[9px] text-[var(--pa-text-dim)]">
                            {meta.label} · {humanSize(f.size)}
                            {vision && <span className="ml-1.5 text-[var(--pa-gold)]">→ vision</span>}
                            {isRealguide && (
                              <span className="ml-1.5 text-emerald-400">→ Real Guide</span>
                            )}
                          </div>
                        </div>
                        {isPhoto && (
                          <select
                            value={f.photo_subtype || ""}
                            onChange={(e) => setFileSubtype(f.id, e.target.value)}
                            className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-[9px] text-[var(--pa-text)] px-1 py-0.5 focus:border-[var(--pa-gold)] focus:outline-none"
                            data-testid={`attach-subtype-${f.id}`}
                          >
                            {PHOTO_SUBTYPE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={() => removeUploadedFile(f.id)}
                          className="p-0.5 rounded hover:bg-[var(--pa-bg-2)] text-[var(--pa-text-dim)] hover:text-red-400"
                          title="Remove"
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-4">
            {def.fields.map((f) => (
              <div key={f.name}>
                <label className="block text-xs font-semibold text-[var(--pa-text-muted)] mb-1.5">
                  {f.label} {f.required && <span className="text-[var(--pa-gold)]">*</span>}
                </label>
                {renderField(f)}
              </div>
            ))}
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="module-submit"
            className="mt-6 w-full bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-6 py-3 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Generating consultation…
              </>
            ) : (
              "Generate consultation"
            )}
          </button>
        </form>

        {/* Result */}
        <div className="lg:col-span-7" data-testid="module-result-area">
          {!result && !loading && (
            <div className="bg-[var(--pa-surface)] border border-dashed border-[var(--pa-border)] rounded-lg p-10 text-center">
              <FileText size={32} className="text-[var(--pa-gold)] mx-auto" />
              <div className="kicker mt-4">AI Consultation</div>
              <h3 className="font-serif text-xl text-[var(--pa-text)] mt-2">
                Specialist-grade analysis appears here
              </h3>
              <p className="mt-2 text-sm text-[var(--pa-text-muted)] max-w-md mx-auto">
                Fill in the clinical inputs on the left and submit. Your consultation will
                be auto-saved to Case History.
              </p>
            </div>
          )}

          {loading && (
            <div className="bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-10 text-center">
              <Loader2 size={28} className="text-[var(--pa-gold)] animate-spin mx-auto" />
              <div className="mt-4 text-sm text-[var(--pa-text-muted)]">
                Consulting Claude Sonnet 4… typically 6–12 seconds.
              </div>
            </div>
          )}

          {result && (
            <div className="bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="kicker">Specialist Analysis</div>
                  <div className="font-serif text-xl text-[var(--pa-text)] mt-1">
                    {result.module_name}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={exportPdf}
                    data-testid="export-pdf"
                    className="flex items-center gap-2 border border-[var(--pa-gold)] text-[var(--pa-gold)] text-sm px-4 py-2 rounded-md hover:bg-[var(--pa-gold)]/10 transition"
                  >
                    <Download size={14} /> PDF
                  </button>
                  <button
                    onClick={() => nav(`/consultation/${result.id}`)}
                    data-testid="open-consultation"
                    className="flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
                  >
                    Open & Request Review →
                  </button>
                </div>
              </div>
              <div className="gold-divider my-5" />
              <AiOutputDisclaimer className="mb-4" />
              <div
                className="prose-clinical"
                data-testid="ai-response"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(result.response) }}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
